import { useMemo, useState } from "react";
import type { GetTransactionsQueryDto } from "../../api/types";

export type TransactionPaging = {
  /// `filters` with the current page's cursor applied. Pass this to
  /// `useTransactions` rather than the raw filters.
  pageFilters: GetTransactionsQueryDto;
  /// 1-based, for display.
  page: number;
  hasPrevious: boolean;
  /// Records the cursor the server returned and advances to that page.
  goToNextPage: (nextCursor: string | null) => void;
  goToPreviousPage: () => void;
};

/// Identity of the result set being paged. The cursor is excluded: it selects
/// a page *within* a result set, whereas everything else defines which set.
const resultSetKey = (filters: GetTransactionsQueryDto) =>
  JSON.stringify({ ...filters, cursor: undefined });

/**
 * Cursor paging for the transaction list.
 *
 * The API is cursor-based, so pages can only be walked forwards; going back
 * means replaying a cursor we have already seen. This keeps the visited
 * cursors in a stack (`[null, cursorOfPage2, cursorOfPage3, …]`) and indexes
 * into it, which gives working Previous/Next without a count endpoint.
 *
 * Changing any filter, the sort, or the limit produces a different result set,
 * which invalidates every stored cursor — the stack resets to page 1.
 */
export const useTransactionPaging = (
  filters: GetTransactionsQueryDto,
): TransactionPaging => {
  const key = resultSetKey(filters);
  const [state, setState] = useState({
    key,
    cursors: [null] as (string | null)[],
    index: 0,
  });

  // Adjusting state during render rather than in an effect: the reset has to
  // land in the same render that sees the new filters, otherwise one request
  // goes out with a cursor from the previous result set.
  const current =
    state.key === key ? state : { key, cursors: [null], index: 0 };
  if (current !== state) setState(current);

  const cursor = current.cursors[current.index] ?? null;

  const pageFilters = useMemo(
    () => ({ ...filters, cursor }),
    [filters, cursor],
  );

  const goToNextPage = (nextCursor: string | null) => {
    if (nextCursor === null) return;
    setState((prev) => {
      const cursors = prev.cursors.slice(0, prev.index + 1);
      cursors.push(nextCursor);
      return { ...prev, cursors, index: prev.index + 1 };
    });
  };

  const goToPreviousPage = () =>
    setState((prev) =>
      prev.index === 0 ? prev : { ...prev, index: prev.index - 1 },
    );

  return {
    pageFilters,
    page: current.index + 1,
    hasPrevious: current.index > 0,
    goToNextPage,
    goToPreviousPage,
  };
};
